/* Native custom elements. No dependencies; reconnect safely in Shopify's editor. */
class RidoraCompare extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector("[data-compare]");
    this.update = () => {
      this.style.setProperty("--position", `${this.input.value}%`);
      this.input.setAttribute("aria-valuetext", `${this.input.value}%`);
    };
    this.input?.addEventListener("input", this.update);
  }
  disconnectedCallback() {
    this.input?.removeEventListener("input", this.update);
  }
}
class RidoraStickyBuy extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest("product-info");
    this.button = this.querySelector("[data-sticky-submit]");
    this.refresh = () => {
      this.original = document.getElementById(
        `ProductSubmitButton-${this.dataset.section}`,
      );
      const source = this.scope?.querySelector(
        `#price-${CSS.escape(this.dataset.section)}`,
      );
      if (!this.original) {
        this.hidden = true;
        return;
      }
      this.button.disabled =
        this.original.disabled ||
        this.original.getAttribute("aria-disabled") === "true";
      this.button.textContent =
        this.original.querySelector("span")?.textContent.trim() ||
        this.original.textContent.trim();
      if (source)
        this.querySelector("[data-sticky-price]").textContent = source
          .querySelector(".price")
          ?.classList.contains("price--on-sale")
          ? source
              .querySelector(".price__sale .price-item--sale")
              .textContent.trim()
          : source.querySelector(".price-item--regular")?.textContent.trim();
    };
    this.measureVisibility = () => {
      const pastButton = this.original?.getBoundingClientRect().bottom < 0;
      this.hidden =
        !pastButton || !window.matchMedia("(max-width: 749px)").matches;
      document.body.classList.toggle("ridora-sticky-visible", !this.hidden);
    };
    this.visibility = () => {
      if (this.visibilityFrame) return;
      this.visibilityFrame = requestAnimationFrame(() => {
        this.visibilityFrame = null;
        this.measureVisibility();
      });
    };
    this.submit = () => {
      this.refresh();
      if (!this.button.disabled) this.original.click();
    };
    this.button.addEventListener("click", this.submit);
    this.refresh();
    // Observe only the real form panel, never this element: avoids a mutation loop.
    const panel = this.scope?.querySelector(
      `[id="ProductInfo-${this.dataset.section}"]`,
    );
    if (panel) {
      this.observer = new MutationObserver(() => {
        this.refresh();
        this.visibility();
      });
      this.observer.observe(panel, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ["disabled", "aria-disabled"],
      });
    }
    window.addEventListener("scroll", this.visibility, { passive: true });
    window.addEventListener("resize", this.visibility, { passive: true });
    window.visualViewport?.addEventListener("resize", this.visibility, {
      passive: true,
    });
    this.visibility();
  }
  disconnectedCallback() {
    this.observer?.disconnect();
    cancelAnimationFrame(this.visibilityFrame);
    window.visualViewport?.removeEventListener("resize", this.visibility);
    this.button?.removeEventListener("click", this.submit);
    window.removeEventListener("scroll", this.visibility);
    window.removeEventListener("resize", this.visibility);
    document.body.classList.remove("ridora-sticky-visible");
  }
}
class RidoraCountdown extends HTMLElement {
  connectedCallback() {
    // Require an explicit ISO timestamp with offset, rather than guessing merchant timezones.
    if (
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?(Z|[+-]\d{2}:\d{2})$/.test(
        this.dataset.date || "",
      )
    )
      return;
    const deadline = Date.parse(this.dataset.date);
    if (!Number.isFinite(deadline)) return;
    this.update = () => {
      const remaining = deadline - Date.now();
      const box = this.querySelector(".r-countdown");
      if (remaining <= 0) {
        box.hidden = true;
        clearInterval(this.timer);
        return;
      }
      box.hidden = false;
      this.querySelector("[data-days]").textContent = Math.floor(
        remaining / 86400000,
      );
      this.querySelector("[data-hours]").textContent = String(
        Math.floor(remaining / 3600000) % 24,
      ).padStart(2, "0");
      this.querySelector("[data-minutes]").textContent = String(
        Math.floor(remaining / 60000) % 60,
      ).padStart(2, "0");
    };
    this.update();
    this.timer = setInterval(this.update, 30000);
  }
  disconnectedCallback() {
    clearInterval(this.timer);
  }
}
if (!customElements.get("ridora-compare"))
  customElements.define("ridora-compare", RidoraCompare);
if (!customElements.get("ridora-sticky-buy"))
  customElements.define("ridora-sticky-buy", RidoraStickyBuy);
if (!customElements.get("ridora-countdown"))
  customElements.define("ridora-countdown", RidoraCountdown);

// One-shot mobile reveals start before content enters view; above-fold content stays visible.
(() => {
  const mobile = matchMedia("(max-width: 749px)");
  const reduced = matchMedia("(prefers-reduced-motion: reduce)");
  let observer;
  function setup(root = document) {
    if (
      document.body.dataset.ridoraMotion !== "true" ||
      window.Shopify?.designMode ||
      reduced.matches
    )
      return;
    const selectors = mobile.matches ? ".r-intro, .scroll-trigger" : ".r-intro";
    observer ||= new IntersectionObserver(
      (entries) => {
        for (const entry of entries)
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            entry.target.dataset.ridoraRevealed = "true";
            observer.unobserve(entry.target);
          }
      },
      {
        rootMargin: mobile.matches ? "0px 0px 100px 0px" : "0px",
        threshold: mobile.matches ? 0 : 0.12,
      },
    );
    root.querySelectorAll(selectors).forEach((node) => {
      if (
        node.dataset.ridoraRevealed ||
        node.closest("header, footer, .r-hero") ||
        node.querySelector("ridora-sticky-buy") ||
        node.closest("product-info")
      )
        return;
      if (
        mobile.matches &&
        node.getBoundingClientRect().top < innerHeight + 100
      ) {
        node.dataset.ridoraRevealed = "true";
        return;
      }
      node.classList.add(mobile.matches ? "r-mobile-reveal" : "r-reveal");
      observer.observe(node);
    });
  }
  function reset() {
    observer?.disconnect();
    observer = undefined;
    document
      .querySelectorAll(".r-mobile-reveal, .r-reveal")
      .forEach((node) =>
        node.classList.remove("r-mobile-reveal", "r-reveal", "is-visible"),
      );
    setup();
  }
  mobile.addEventListener("change", reset);
  reduced.addEventListener("change", reset);
  document.addEventListener("shopify:section:load", (event) =>
    setup(event.target),
  );
  setup();
})();
